/**
 * RTL-WALLET-SELECTOR-2 (2026-08-24) — runtime adapter identity guard.
 *
 * Reason this file exists:
 * `@solana/wallet-adapter-react@0.15.39` resolves `select(name)` by a
 * pure name string (`adapters.find(a => a.name === walletName)` — see
 * `wallet-adapter-react/lib/esm/WalletProvider.js:54`), and the shipped
 * `WalletMultiButton` modal dedupes rows by `adapter.name`. Backpack —
 * when installed alongside Phantom — can register a Wallet Standard
 * entry that reports `name === "Phantom"` as a compat shim. When
 * Backpack's registration is first in the discovery order, clicking
 * "Phantom" in the modal resolves to Backpack's shim adapter and
 * Backpack opens for the sign popup. The previous pubkey-only pinning
 * cannot detect this (a user who imported the same seed into both
 * wallets has the same pubkey on both adapters).
 *
 * The Wallet Standard `Wallet` object exposes `url` and `icon` from the
 * wallet's OWN declared metadata. Backpack cannot proxy its `url` to
 * `phantom.app`; that string is baked into Phantom's extension.
 * `url` is therefore a positive identity signal we can check.
 *
 * This module provides:
 *   * `getExpectedUrlPattern(name)` — regex for the wallet's known
 *     canonical homepage. Callers use it to positively assert
 *     `adapter.url` matches the display name.
 *   * `assertAdapterIdentity(adapter)` — throws
 *     `WALLET_IDENTITY_MISMATCH` if the adapter's `name` is a
 *     well-known wallet but its `url` does not match.
 *   * `walletDebugLog()` — dumps the adapters visible via
 *     `useWallet().wallets` when `?wallet-debug=1` is present in the
 *     URL, so operators can prove at runtime what the browser is
 *     actually resolving. Does NOT log secrets, message contents,
 *     signatures, or public keys.
 *
 * Non-goals: replace the wallet-adapter-react-ui modal, patch
 * wallet-adapter-react internals, or interact with Wallet Standard
 * directly. Those are much larger changes and belong to a subsequent
 * pass if this defensive layer proves insufficient.
 */

// Canonical homepage URLs baked into each wallet's Wallet-Standard
// registration. Regexes are intentionally loose (host-only, ignore
// trailing paths / subdomain variants like www.). Add new wallets
// here as we officially support them.
const KNOWN_WALLET_URL_PATTERNS = Object.freeze({
  Phantom: /(^|\/\/([^/]+\.)?)phantom\.app(\/|$)/i,
  Backpack: /(^|\/\/([^/]+\.)?)backpack\.app(\/|$)/i,
  Solflare: /(^|\/\/([^/]+\.)?)solflare\.com(\/|$)/i,
  Glow: /(^|\/\/([^/]+\.)?)glow\.app(\/|$)/i,
});

export function getExpectedUrlPattern(walletName) {
  return KNOWN_WALLET_URL_PATTERNS[walletName] || null;
}

/**
 * DISABLED — URL-based identity check retired 2026-08-24 after
 * production forensics conclusively established
 * `StandardWalletAdapter.url` is a HARDCODED constant
 * ("https://github.com/solana-labs/wallet-standard") baked into
 * `@solana/wallet-standard-wallet-adapter-base/lib/esm/adapter.js`
 * lines 25-27. Genuine Phantom, genuine Backpack, and any shim all
 * report the same GitHub URL, so URL matching is a false-positive
 * on every wrapped wallet. Live evidence:
 *   * Backpack underlying wallet: name="Backpack", vendor feature `backpack:*`
 *   * Phantom underlying wallet:  name="Phantom",  vendor feature `phantom:*`
 * No spoofing detected — the Wallet Standard registry is legitimate.
 *
 * Function signature preserved so existing call sites in
 * WalletAuthContext + EliminationLink don't need synchronous
 * removal; body now only fails-closed on a truly missing adapter
 * (the pinned-adapter pubkey check upstream already covers that,
 * but keep the belt-and-braces guard). NO URL check. NO name check.
 * NO throw for present adapters.
 *
 * When a positive-identity check is reinstated it MUST use
 * `adapter.wallet.features` vendor namespaces (`phantom:*` /
 * `backpack:*`) — those are baked into each extension's own
 * `registerWallet()` call and cannot be spoofed at runtime without
 * breaking the actual sign path. Do not resurrect URL matching.
 */
export function assertAdapterIdentity(adapter) {
  if (!adapter) {
    throw new Error('WALLET_IDENTITY_MISMATCH: no active wallet adapter.');
  }
  // Intentionally empty — see block comment above. Keep this function
  // as a hookable seam so future evidence-based rules can slot in
  // without another call-site edit.
}

// -- Adapter description helpers (module-private) --------------------
//
// Extracted from walletDebugLog so the same enriched shape can be
// emitted for a single pinned adapter via walletAdapterForensics.

const _describeUnderlying = (w) => {
  if (!w || typeof w !== 'object') return null;
  const featureKeys = w.features && typeof w.features === 'object'
    ? Object.keys(w.features)
    : null;
  return {
    name: w.name,
    version: w.version,
    chains: Array.isArray(w.chains) ? w.chains.slice() : null,
    iconLen: String(w.icon || '').length,
    iconPrefix: String(w.icon || '').slice(0, 80),
    featureKeys,
    // Vendor-namespaced features are the strongest positive identity
    // signal — a Backpack shim cannot expose `phantom:*` features and
    // still function; a Phantom shim cannot expose `backpack:*` and
    // still function.
    vendorFeatureKeys: featureKeys
      ? featureKeys.filter((k) => !k.startsWith('standard:'))
      : null,
    accountsCount: Array.isArray(w.accounts) ? w.accounts.length : null,
  };
};

// Simple non-cryptographic string hash. Purpose: derive a short
// icon fingerprint the operator can eyeball to prove two adapters
// (mount-time vs pre-sign) are the SAME adapter object, without
// pasting an entire 20 KB base64 data URI into a screenshot.
const _shortHash = (s) => {
  const str = String(s || '');
  let h = 0;
  for (let i = 0; i < str.length; i += 1) {
    h = ((h << 5) - h + str.charCodeAt(i)) | 0;
  }
  return `h${(h >>> 0).toString(16).padStart(8, '0')}`;
};

const _describeAdapter = (adapter) => {
  if (!adapter) return null;
  const proto = Object.getPrototypeOf(adapter) || {};
  // StandardWalletAdapter's underlying Wallet Standard `Wallet` is
  // exposed under `wallet` (public getter) in the installed
  // `@solana/wallet-standard-wallet-adapter-base`. Older/newer
  // versions have used `_wallet` / `_underlyingWallet`. Probe all
  // three; first non-null wins.
  const underlying =
    adapter._wallet ||
    adapter.wallet ||
    adapter._underlyingWallet ||
    null;
  const underlyingKey = adapter._wallet
    ? '_wallet'
    : (adapter.wallet
      ? 'wallet'
      : (adapter._underlyingWallet ? '_underlyingWallet' : null));
  let ownKeys = [];
  try { ownKeys = Object.getOwnPropertyNames(adapter); } catch { /* ignore */ }
  let protoKeys = [];
  try {
    protoKeys = Object.getOwnPropertyNames(proto).filter((k) => k !== 'constructor');
  } catch { /* ignore */ }
  let supportedTxVersions = null;
  try {
    supportedTxVersions = adapter.supportedTransactionVersions
      ? Array.from(adapter.supportedTransactionVersions)
      : null;
  } catch { /* ignore */ }
  const iconStr = String(adapter.icon || '');
  return {
    name: adapter.name,
    url: adapter.url,
    iconLen: iconStr.length,
    iconPrefix: iconStr.slice(0, 80),
    iconFingerprint: _shortHash(iconStr),
    ctor: proto && proto.constructor ? proto.constructor.name : null,
    readyState: adapter.readyState || null,
    // Public-key identity is already public on-chain — safe to log.
    // Presence + address tell us whether the pinned adapter has an
    // account attached, which is a prerequisite for signMessage.
    publicKey: adapter.publicKey ? adapter.publicKey.toBase58() : null,
    connected: adapter.connected === true,
    connecting: adapter.connecting === true,
    supportedTxVersions,
    ownKeys,
    protoKeys,
    underlyingKey,
    underlyingWallet: _describeUnderlying(underlying),
    // `signMessage` presence proves this adapter (not a different
    // one) is the signer that will actually be invoked.
    hasSignMessage: typeof adapter.signMessage === 'function',
  };
};

/**
 * Point-of-use forensics for a SINGLE pinned adapter variable.
 *
 * RTL-WALLET-SELECTOR-2 (2026-08-24) — evidence from production
 * showed `walletDebugLog('siws-pre-nonce', wallet)` logged
 * `currently selected: null` because the destructured `wallet`
 * variable in WalletAuthContext / EliminationLink is the
 * wallet-adapter-react `Wallet | null` union, NOT the full
 * `WalletContextState`. walletDebugLog expected the context
 * (with `.wallets` and `.wallet` fields) so it saw undefined and
 * emitted null — while the guard `assertAdapterIdentity(adapter)`
 * on the very next line received a live "Phantom" adapter and
 * threw. Same-instant, mutually contradictory reports.
 *
 * `walletAdapterForensics` takes the exact adapter object about
 * to be handed to a security-sensitive call (assertion, signing)
 * and dumps its enriched metadata via _describeAdapter. Callers
 * pass the pinned local variable directly — not the outer wallet
 * context — so there is no ambiguity about what is being logged.
 *
 * `extra` allows the caller to include an ad-hoc context hint
 * (e.g. `{ expectedAddress, step }`) so the same production
 * screenshot proves the address the app THINKS it's about to sign
 * with matches the adapter's actual `publicKey`.
 *
 * Gated on `?wallet-debug=1` — no output for regular users.
 */
export function walletAdapterForensics(source, adapter, extra) {
  try {
    if (typeof window === 'undefined') return;
    const params = new URLSearchParams(window.location.search || '');
    if (params.get('wallet-debug') !== '1') return;
    const payload = {
      source,
      when: new Date().toISOString(),
      extra: (extra && typeof extra === 'object') ? extra : null,
      adapter: _describeAdapter(adapter),
    };
    console.log(
      `[wallet-debug/${source}] pinned adapter:\n` + JSON.stringify(payload, null, 2)
    );
  } catch { /* diagnostics must never throw */ }
}

// -- Invocation-layer forensics --------------------------------------
//
// Evidence gathered so far (RTL-WALLET-SELECTOR-2, 2026-08-24):
// The adapter that reaches `adapter.signMessage(...)` IS the genuine
// Phantom `StandardWalletAdapter` wrapping the real Phantom Wallet-
// Standard entry (name="Phantom", vendor feature keys "phantom:*",
// stable Phantom icon fingerprint). Yet clicking Phantom in
// production causes Backpack's approval popup to open. The next
// diagnostic pass must reach BELOW the adapter layer:
//   * What is the concrete `wallet.features["solana:signMessage"]`
//     function object? Its `.toString()` reveals whether it is
//     `[native code]` (extension-boundary IPC — cannot be spoofed
//     from userland) or JS source that internally delegates to
//     `window.solana` / `window.phantom.solana` / a shared postMessage
//     channel that Backpack can intercept.
//   * Do `window.solana`, `window.phantom.solana`, `window.backpack`
//     currently exist, and do they identify as Phantom, Backpack, or
//     xNFT? Are `window.solana` and `window.phantom.solana` the SAME
//     object (Backpack "default-wallet" mode hijacks both), or
//     different?
// walletProviderForensics dumps exactly that, using the same pinned
// adapter reference passed to `walletAdapterForensics`. Gated on
// `?wallet-debug=1`. Never logs secrets, message contents, or
// signatures — only public identity metadata already visible to any
// on-page script.

const _safeFnDescribe = (fn) => {
  if (typeof fn !== 'function') {
    return { type: typeof fn, present: fn != null };
  }
  let source;
  try {
    // `Function.prototype.toString` returns `function name() { [native code] }`
    // for host / bound / built-in functions and readable source for
    // regular JS functions. Truncated to 300 chars so a data URI or
    // long lambda cannot flood the console screenshot.
    source = String(fn.toString()).slice(0, 300);
  } catch { source = '(toString threw)'; }
  return {
    type: 'function',
    name: fn.name || null,
    length: typeof fn.length === 'number' ? fn.length : null,
    isNative: /\[native code\]/.test(source),
    isBound: /^function bound /.test(source),
    sourcePrefix: source,
  };
};

const _safeProviderDescribe = (label, provider) => {
  if (!provider || typeof provider !== 'object') {
    return { label, present: false, ref: null };
  }
  const out = { label, present: true };
  // Widely-used vendor identity flags. All are booleans the
  // extensions themselves set on their own provider objects to
  // advertise identity to dApps.
  for (const flag of ['isPhantom', 'isBackpack', 'isXnft', 'isXNFT', 'isSolflare', 'isGlow', 'isMagicEden', 'isMetaMask']) {
    if (flag in provider) out[flag] = Boolean(provider[flag]);
  }
  // Publish enumerable own-property keys so an operator can see if a
  // provider silently gained/lost methods. Safe — just names.
  try { out.ownKeys = Object.getOwnPropertyNames(provider).slice(0, 40); } catch { out.ownKeys = null; }
  try {
    const proto = Object.getPrototypeOf(provider);
    out.protoCtor = proto && proto.constructor ? proto.constructor.name : null;
    out.protoKeys = proto ? Object.getOwnPropertyNames(proto).filter((k) => k !== 'constructor').slice(0, 40) : null;
  } catch { /* ignore */ }
  // Public-key on the provider — safe (on-chain). Presence + address
  // tell us whether the shared provider believes it is signed in.
  try {
    out.publicKey = provider.publicKey && typeof provider.publicKey.toBase58 === 'function'
      ? provider.publicKey.toBase58()
      : (provider.publicKey ? String(provider.publicKey) : null);
  } catch { /* ignore */ }
  out.connected = provider.connected === true;
  out.isConnected = provider.isConnected === true;
  // `signMessage` is the exact function that a Phantom-classic-
  // adapter-style call site would invoke — capture its identity too.
  out.signMessage = _safeFnDescribe(provider.signMessage);
  out.signAndSendTransaction = _safeFnDescribe(provider.signAndSendTransaction);
  return out;
};

export function walletProviderForensics(source, adapter, extra) {
  try {
    if (typeof window === 'undefined') return;
    const params = new URLSearchParams(window.location.search || '');
    if (params.get('wallet-debug') !== '1') return;

    // 1. The Wallet Standard feature function that
    //    `StandardWalletAdapter.signMessage` actually invokes at
    //    line 362-365 of the installed dep.
    const underlying =
      adapter?._wallet ||
      adapter?.wallet ||
      adapter?._underlyingWallet ||
      null;
    const feature = underlying?.features
      ? (underlying.features['solana:signMessage']
        || underlying.features['solana:signIn']
        || null)
      : null;
    const featureFn = feature?.signMessage || feature?.signIn || null;

    // 2. Cross-check the vendor namespaces that classic adapters use.
    //    `window.phantom?.solana` is Phantom's dedicated namespace.
    //    `window.solana` is the shared "default Solana wallet"
    //    namespace — Backpack, when set as default, replaces this
    //    with its own provider (documented in Backpack's compat mode
    //    behaviour).
    const winSolana = (typeof window !== 'undefined') ? window.solana : null;
    const winPhantomSolana = (typeof window !== 'undefined') ? window.phantom?.solana : null;
    const winBackpack = (typeof window !== 'undefined') ? window.backpack : null;

    const payload = {
      source,
      when: new Date().toISOString(),
      extra: (extra && typeof extra === 'object') ? extra : null,
      // Feature-object identity — the function
      // `StandardWalletAdapter._signMessage` will actually call.
      standardFeature: {
        featureKey: feature?.signMessage ? 'solana:signMessage' : (feature?.signIn ? 'solana:signIn' : null),
        version: feature?.version || null,
        signMessageFn: _safeFnDescribe(featureFn),
      },
      // Namespace-provider identities — reveal Backpack hijack of
      // the shared / vendor providers, if any.
      winSolanaSameAsWinPhantomSolana: winSolana != null && winPhantomSolana != null && winSolana === winPhantomSolana,
      windowSolana: _safeProviderDescribe('window.solana', winSolana),
      windowPhantomSolana: _safeProviderDescribe('window.phantom.solana', winPhantomSolana),
      windowBackpack: _safeProviderDescribe('window.backpack', winBackpack),
    };
    console.log(
      `[wallet-debug/${source}] invocation layer:\n` + JSON.stringify(payload, null, 2)
    );
  } catch { /* diagnostics must never throw */ }
}

/**
 * Development-only diagnostic. Gated on `?wallet-debug=1` query
 * param so it never fires for normal users. Emits TWO multi-line
 * JSON strings per call (discovered wallets + currently selected)
 * so an operator can screenshot the full payload without clicking
 * any ▶ expanders. Deliberately does NOT log secrets, message
 * contents, or signatures — only public identity metadata that
 * browsers already expose to any dApp on the page.
 */
export function walletDebugLog(source, walletCtx) {
  try {
    if (typeof window === 'undefined') return;
    const params = new URLSearchParams(window.location.search || '');
    if (params.get('wallet-debug') !== '1') return;
    const wallets = Array.isArray(walletCtx?.wallets) ? walletCtx.wallets : [];
    const rows = wallets.map((w, i) => ({
      i,
      readyState: w?.readyState,
      adapter: _describeAdapter(w?.adapter),
    }));
    const sel = walletCtx?.wallet;
    // Emit as a plain multi-line JSON string so ONE screenshot
    // captures the full payload — the non-developer operator does
    // not need to click any DevTools ▶ expanders.
    console.log(
      `[wallet-debug/${source}] discovered wallets:\n` + JSON.stringify(rows, null, 2)
    );
    console.log(
      `[wallet-debug/${source}] currently selected:\n` +
      JSON.stringify(
        sel
          ? { readyState: sel.readyState, adapter: _describeAdapter(sel.adapter) }
          : null,
        null,
        2
      )
    );
  } catch { /* diagnostics must never throw */ }
}
